import { useCallback, useState, type ChangeEvent, type DragEvent } from "react";
import { api, ApiError, type Settings, type UploadRecord } from "../lib/api";
import { copyToClipboard, formatBytes } from "../lib/format";
import { useToast } from "./Toast";

type ItemState = "pending" | "uploading" | "done" | "failed";

interface QueueItem {
  id: string;
  name: string;
  state: ItemState;
  detail: string;
}

interface Props {
  settings: Settings | null;
  disabled: boolean;
  onUploaded: (record: UploadRecord) => void;
}

/**
 * The upload surface. It accepts a drop, a file picker and a paste, because an
 * image on its way to a blog post arrives by all three routes.
 */
export function UploadDropzone({ settings, disabled, onUploaded }: Props) {
  const { notify } = useToast();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const isBusy = queue.some((item) => item.state === "uploading");

  const updateItem = useCallback((id: string, changes: Partial<QueueItem>) => {
    setQueue((current) =>
      current.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    );
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (disabled || files.length === 0) return;

      const items: QueueItem[] = files.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        state: "pending",
        detail: formatBytes(file.size),
      }));
      setQueue(items);

      // Sequential on purpose: uploads pass through one Cloudreve account, and
      // a burst of parallel sessions is more likely to trip its rate limits
      // than it is to finish faster.
      let lastRecord: UploadRecord | null = null;
      for (const [index, file] of files.entries()) {
        const item = items[index];
        updateItem(item.id, { state: "uploading", detail: "uploading" });
        try {
          const record = await api.uploads.create(file);
          updateItem(item.id, { state: "done", detail: "filed" });
          onUploaded(record);
          lastRecord = record;
        } catch (cause) {
          const message =
            cause instanceof ApiError ? cause.message : "upload failed";
          updateItem(item.id, { state: "failed", detail: message });
          notify(`${file.name}: ${message}`, "error");
        }
      }

      // Getting the URL is the point, so put the last one on the clipboard.
      if (lastRecord) {
        const copied = await copyToClipboard(lastRecord.url);
        notify(
          copied
            ? `Filed ${files.length} image${files.length > 1 ? "s" : ""}. Link copied.`
            : `Filed ${files.length} image${files.length > 1 ? "s" : ""}.`,
          "success",
        );
      }

      // Clear the queue once it has been read, but leave failures on screen.
      window.setTimeout(() => {
        setQueue((current) => current.filter((entry) => entry.state === "failed"));
      }, 2500);
    },
    [disabled, notify, onUploaded, updateItem],
  );

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    void uploadFiles(Array.from(event.dataTransfer.files));
  }

  function handlePick(event: ChangeEvent<HTMLInputElement>) {
    void uploadFiles(Array.from(event.target.files ?? []));
    // Reset so picking the same file twice fires a change event again.
    event.target.value = "";
  }

  const accept = settings?.allowed_mime.join(",") ?? "image/*";
  const limit = settings ? formatBytes(settings.max_size_bytes) : "";

  return (
    <div
      className={`dropzone${isDragging ? " is-dragging" : ""}${isBusy ? " is-busy" : ""}`}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onPaste={(event) => {
        const files = Array.from(event.clipboardData.files);
        if (files.length > 0) void uploadFiles(files);
      }}
    >
      <input
        type="file"
        multiple
        accept={accept}
        onChange={handlePick}
        disabled={disabled || isBusy}
        aria-label="Choose images to upload"
      />

      {queue.length > 0 ? (
        <div className="dropzone__queue">
          {queue.map((item) => (
            <div className="dropzone__item" key={item.id} data-state={item.state}>
              <span>{item.name}</span>
              <span>{item.detail}</span>
            </div>
          ))}
        </div>
      ) : (
        <>
          <p className="dropzone__headline">
            {disabled ? "Connect Cloudreve to upload" : "Drop images here"}
          </p>
          <p className="dropzone__hint">
            {disabled
              ? "Authorise the service in the connection panel first."
              : `Or click to choose, or paste from the clipboard.${limit ? ` Up to ${limit} each.` : ""}`}
          </p>
        </>
      )}
    </div>
  );
}
